<mat-card class="page-card">
  <section class="page-header">
    <div class="page-header-content">
      <div class="page-header-icon">
        <mat-icon>person</mat-icon>
      </div>
      <div class="page-header-text">
        <span class="eyebrow">{{ 'myProfile.banner.eyebrow' | transloco }}</span>
        <h1>{{ 'myProfile.banner.title' | transloco }}</h1>
        <p>{{ 'myProfile.banner.description' | transloco }}</p>
      </div>
    </div>
  </section>

  <mat-card-content>
    <section class="content-section">
      <div class="section-header">
        <h2>{{ 'myProfile.personalData.heading' | transloco }}</h2>
      </div>

      <form [formGroup]="personalDataForm" class="form-grid">
        <mat-form-field appearance="outline" class="span-3" subscriptSizing="dynamic">
          <mat-label>{{ 'dataManagement.organizationSettings.fields.firstName' | transloco }}</mat-label>
          <input matInput formControlName="firstName" required />
          @if (personalDataForm.controls.firstName.hasError('required')) {
            <mat-error>{{ 'dataManagement.userData.userDialog.validation.required' | transloco }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="span-3" subscriptSizing="dynamic">
          <mat-label>{{ 'dataManagement.organizationSettings.fields.lastName' | transloco }}</mat-label>
          <input matInput formControlName="lastName" required />
          @if (personalDataForm.controls.lastName.hasError('required')) {
            <mat-error>{{ 'dataManagement.userData.userDialog.validation.required' | transloco }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="span-3" subscriptSizing="dynamic">
          <mat-label>{{ 'dataManagement.userData.columns.email' | transloco }}</mat-label>
          <input matInput formControlName="email" required />
          @if (personalDataForm.controls.email.hasError('required')) {
            <mat-error>{{ 'dataManagement.userData.userDialog.validation.required' | transloco }}</mat-error>
          } @else if (personalDataForm.controls.email.hasError('email')) {
            <mat-error>{{ 'dataManagement.userData.userDialog.validation.email' | transloco }}</mat-error>
          }
          @if (isEmailManaged()) {
            <mat-hint>{{ 'myProfile.personalData.emailHelpSso' | transloco }}</mat-hint>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="span-3" subscriptSizing="dynamic">
          <mat-label>{{ 'myProfile.personalData.languageLabel' | transloco }}</mat-label>
          <mat-select [value]="currentLanguageCode" (selectionChange)="setLanguage($event.value)">
            @for (countryCode of countryCodes; track countryCode) {
              <mat-option [value]="countryCode">
                <span class="flag-icon flag-icon-{{ countryCode }} space-right"></span>
                {{ 'language.' + countryCode | transloco }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
      </form>

      <div class="section-actions">
        <button mat-raised-button color="primary" type="button" [disabled]="isLoadingProfile() || isSavingPersonalData()" (click)="savePersonalData()">
          <mat-icon>check</mat-icon>
          {{ 'common.buttons.save' | transloco }}
        </button>
      </div>
    </section>

    <section class="content-section">
      <div class="section-header">
        <h2>{{ 'myProfile.security.heading' | transloco }}</h2>
      </div>

      @if (isSso() === false) {
        <form [formGroup]="securityForm" class="form-grid">
          <mat-form-field appearance="outline" class="span-4" subscriptSizing="dynamic">
            <mat-label>{{ 'dataManagement.userData.columns.oldPassword' | transloco }}</mat-label>
            <input matInput formControlName="oldPassword" type="password" autocomplete="off" />
            @if (submittedSecurityForm() && securityForm.controls.oldPassword.hasError('required')) {
              <mat-error>{{ 'dataManagement.userData.userDialog.validation.required' | transloco }}</mat-error>
            }
          </mat-form-field>

          <div class="span-8">
            <app-password-form formControlName="newPassword" [submitted]="submittedSecurityForm()"></app-password-form>
          </div>
        </form>

        <div class="section-actions">
          <button mat-stroked-button color="primary" type="button" [disabled]="isChangingPassword()" (click)="changePassword()">
            <mat-icon>key</mat-icon>
            {{ 'dataManagement.userData.texts.changePassword' | transloco }}
          </button>
        </div>
      } @else if (isSso()) {
        <div class="sso-info">
          <mat-icon>key</mat-icon>
          <div>
            <strong>{{ 'myProfile.security.ssoTitle' | transloco }}</strong>
            <span>{{ 'myProfile.security.ssoDescription' | transloco }}</span>
          </div>
        </div>
      }
    </section>

    <section class="content-section">
      <div class="section-header">
        <h2>{{ 'myProfile.organization.heading' | transloco }}</h2>
      </div>

      <div class="facts-grid">
        <div class="fact-item">
          <span class="fact-label">{{ 'myProfile.organization.facts.organization' | transloco }}</span>
          <span class="fact-value">{{ organization().name }}</span>
        </div>
        <div class="fact-item">
          <span class="fact-label">{{ 'myProfile.organization.facts.role' | transloco }}</span>
          <span class="fact-value">{{ roleLabelKey | transloco }}</span>
        </div>
        <div class="fact-item">
          <span class="fact-label">{{ 'myProfile.organization.facts.license' | transloco }}</span>
          <span class="fact-value">{{ (isPremiumUser ? 'myProfile.organization.licensePremium' : 'myProfile.organization.licenseStandard') | transloco }}</span>
        </div>
      </div>
    </section>

    <section class="content-section">
      <div class="section-header">
        <h2>{{ 'myProfile.organization.groups.heading' | transloco }}</h2>
      </div>

      @for (group of myGroups(); track group.id) {
        <div class="fact-item group-item">
          <span class="fact-label">{{ group.name }}</span>
          <mat-chip-set [attr.aria-label]="group.name">
            @for (member of group.members; track member.id) {
              <mat-chip>{{ member.name }}</mat-chip>
            }
          </mat-chip-set>
        </div>
      } @empty {
        <p class="section-description">{{ 'myProfile.organization.groups.empty' | transloco }}</p>
      }
    </section>
  </mat-card-content>
</mat-card>
